import {
  ChangeDetectionStrategy,
  Component,
  inject,
  OnInit,
  signal,
} from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  Validators,
} from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import {
  AuthService,
  Country,
  Installer,
  InstallersService,
  Language,
} from '@ubie/core';
import { FeedbackService, FormHelper } from '@ubie/shared';

@Component({
  selector: 'acl-user-form',
  standalone: false,
  templateUrl: './user-form-component.component.html',
  styleUrl: './user-form-component.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UserFormComponent implements OnInit {
  private readonly feedbackService = inject(FeedbackService);
  private readonly fb = inject(FormBuilder);
  private readonly route = inject(ActivatedRoute);
  private readonly translateService = inject(TranslateService);
  private readonly authService = inject(AuthService);
  private readonly installerService = inject(InstallersService);

  readonly userForm = this.fb.group({
    companyname: [''],
    vatno: [''],
    firstname: ['', Validators.required],
    lastname: ['', Validators.required],
    streetNr: ['', Validators.required],
    postalcode: ['', Validators.required],
    city: ['', Validators.required],
    country: new FormControl<Country | undefined>(
      undefined,
      Validators.required,
    ),
    language: new FormControl<Language | undefined>(
      undefined,
      Validators.required,
    ),
  });

  readonly countriesData = signal<Country[]>([]);
  readonly languageData = signal<Language[]>([]);

  readonly formHelper = new FormHelper(this.userForm);
  readonly isFormSubmitAttempted = signal(false);

  readonly addressFields = [
    { labelKey: 'profile_street', controlName: 'streetNr' },
    { labelKey: 'profile_postalcode', controlName: 'postalcode' },
    { labelKey: 'profile_city', controlName: 'city' },
  ] as const;

  // FORM FEEDBACK VARS
  // ****************************

  //user object for update
  userSelected: Installer | undefined;
  user: any;

  ngOnInit(): void {
    // get resolved data

    this.countriesData.set(this.route.snapshot.data['countries'] ?? []);
    this.languageData.set(this.route.snapshot.data['languages']);

    const id = this.authService.getUserId();
    if (id) {
      this.getLoggedInUser(id);
    }
  }

  setFormValues(): void {
    // set selected installer
    const selectedCountry = this.countriesData().find(
      (country) => country.id === this.user.country.id,
    );
    const selectedLanguage = this.languageData().find(
      (lang) => lang.id === this.user.language.id,
    );

    // set form values
    this.userForm.setValue(
      {
        firstname: this.user.firstname,
        lastname: this.user.lastname,
        companyname: this.user.companyname,
        vatno: this.user.vatno,
        streetNr: this.user.streetNr,
        postalcode: this.user.postalCode,
        city: this.user.city,
        country: selectedCountry,

        language: selectedLanguage,
      },
      {
        emitEvent: false,
      },
    );
  }

  updateUser(): void {
    this.isFormSubmitAttempted.set(true);
    if (this.userForm.valid) {
      const oldLang = this.userSelected?.language;

      const user = Object.assign({}, this.userSelected, this.userForm.value);
      user.id = this.user.id;
      user.telephone = this.user.telephone;
      user.activated = this.user.activated;
      user.email = this.user.email;

      this.installerService.updateInstaller(user).subscribe({
        next: () => {
          this.isFormSubmitAttempted.set(false);

          this.feedbackService.present(
            'profile_updateSucceeded',
            5000,
            true,
            'medium',
          );

          if (user.language.id !== oldLang?.id) {
            const lang = 'admin_' + user.language.id.toString();
            this.translateService.use(lang);
            this.user.language = user.language;
          }
        },
        error: (error) => {
          this.feedbackService.present(
            error.errorMessage,
            5000,
            true,
            'danger',
          );
        },
      });
    }
  }

  private getLoggedInUser(id: number): void {
    this.installerService.getInstaller(id).subscribe({
      next: (user) => {
        this.user = user;
        this.setFormValues();
      },
      error: (error) => {
        this.feedbackService.present(error);
      },
    });
  }

  /**
   * reset form
   */
  resetForm(userForm: FormGroup): void {
    userForm.reset();
  }
}
